import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { HttpClient } from '@angular/common/http';
import { EMPTY, Observable, from, of } from 'rxjs';
import { map, switchMap, catchError, filter, concatMap, withLatestFrom, timeout } from 'rxjs/operators';
import { Action, Store } from '@ngrx/store';
import {
  addIdMapping,
  executeOperation,
  loadAppData,
  markOperationExecuted,
  markOperationExecutedWithError,
  replaceTempIds,
  showOfflineDeletionPopOver,
  showOfflineDeletionPopOverSuccess,
  syncData,
} from '../actions/offline.action';
import { ConnectionState } from '../reducers/offline.reducer';
import { StorageSyncActions, fetchState } from '../storage-sync.module';
import { loadFamilyMember } from '../actions/family-members.action';
import { loadFarm } from '../actions/farm.action';
import { loadPlots } from '../actions/plots.action';

import { loadUnit } from '../actions/units-action';
import { PopoverComponent, PopoverDataService } from 'src/app/widgets/popover/popover.component';
import { ToastUtils } from '../../toast_builder';
import { cloneDeep } from 'lodash';
import { replaceTempIdsInArray, replaceTempIdsInObject, replaceTempIdsInUrl } from '../../id-mapping-utils';
import { IdMapping, OfflineOperation } from 'src/app/models/offlineOperation.model';
import { getDashboardData, loadTotalAccountingCategories } from '../actions/dashboard.action';
import { loadAgriProduct, loadSelectedAgriProduct } from '../actions/agri-product.action';
import { loadAnimal, loadSelectedAnimal } from '../actions/animals.action';
import { loadPartner, loadPartnerType } from '../actions/partner.action';

@Injectable()
export class OfflineEffect {
  private syncPopover: HTMLIonPopoverElement;

  constructor(
    private actions$: Actions,
    private http: HttpClient,
    private store: Store<{
      connection: ConnectionState;
    }>,
    private toastUtils: ToastUtils,
    private popoverDataService: PopoverDataService
  ) {
    // setInterval(() => {
    //   this.store.dispatch({ type: '[Connection] RETRY_FAILED_OPERATIONS' });
    // }, 60000); // Check every minute
  }
  // Effect to hydrate the state from storage
  hydrate$ = createEffect(
    (): Observable<any> =>
      this.actions$.pipe(
        ofType(StorageSyncActions.NGRX_EFFECT_INIT),
        switchMap(() =>
          from(fetchState()).pipe(
            map((state) => ({
              type: StorageSyncActions.HYDRATED,
              payload: state,
            })),
            catchError((e) => {
              console.warn(
                `error fetching data from store for hydration: ${e}`
              );
              return of({
                type: StorageSyncActions.HYDRATED,
                payload: {},
              });
            })
          )
        )
      )
  );
  private pendingOperations = 0;

  private lastEntitiesLength = 0;
  private completedOperations = 0;
  private totalOperations = 0;

  showOfflineDeletionPopOver$ = createEffect(() =>
    this.actions$.pipe(
      ofType(showOfflineDeletionPopOver),
      switchMap(() =>
        from(this.toastUtils.popOverBuilder(
          {
            key1: 'app_offline_delete_denied',
            canBeClosed: true,
            is_confirm_needed: true,
            is_info_popover: false,
            is_loader_needed: false,
          },
          PopoverComponent,
          'popover-custom',
          null,
          true,
          true
        )).pipe(map(() => showOfflineDeletionPopOverSuccess()))
      )
    ));

  executeOperations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(syncData),
      switchMap(() => this.store.select('connection').pipe(
        filter((connection) => {
          const shouldSync = connection.isOnline && connection.connectionQuality !== 'offline';
          const currentLength = Object.keys(connection.entities).length;
          if (shouldSync) {
            if (this.totalOperations == 0 || Object.values(connection.entities).length == 0) {
              this.totalOperations = Object.values(connection.entities).length
            }
            this.pendingOperations = Object.values(connection.entities).length;

          } else {
            this.pendingOperations = 0;
          }

          const hasChanged = currentLength !== this.lastEntitiesLength;
          return shouldSync && hasChanged;
        }),
        switchMap(async (connection) => {
          const operations = Object.values(connection.entities);
          this.lastEntitiesLength = operations.length;

          if (!this.syncPopover && this.pendingOperations != 0) {
            this.completedOperations = 0; // Reset completed operations count

            this.syncPopover = await this.toastUtils.popOverBuilder(
              {
                key1: 'app_offline_info_sync',
                canBeClosed: true,
                is_confirm_needed: false,
                is_info_popover: false,
                is_loader_needed: true,
                progress: 0,
                totalOperations: this.totalOperations
              },
              PopoverComponent,
              'popover-custom',
              null,
              false,
              true
            );
          } else if (this.syncPopover && (this.pendingOperations === 0 || !connection.isOnline)) {
            this.popoverDataService.updateInputData({
              progress: this.pendingOperations,
              totalOperations: this.pendingOperations,
              completedOperations: this.pendingOperations
            });
            this.syncPopover.dismiss();
            this.syncPopover = null;
          }

          if (operations[0]) {
            return executeOperation({ operation: operations[0] });
          } else {
            this.tempIds = [];
          }
          // else {
          //   return from(null)
          // }
        })))
    )
  );

  sendRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(executeOperation),
      withLatestFrom(this.store.select('connection')),
      switchMap(([value, connection]: [any, any]) => {
        let operation = value.operation;

        // Replace temporary IDs in formDataObject if present
        if (operation?.formDataObject) {
          let updatedFormData = operation.formDataObject;
          // if (Object.keys(connection.idMappings).length > 0) {
          if (this.tempIds.length > 0) {
            updatedFormData = replaceTempIdsInArray(operation.formDataObject, this.tempIds);
          }
          // Create new FormData
          const newFormData = new FormData();
          for (const key in updatedFormData) {
            newFormData.append(key, updatedFormData[key]);
          }
          console.log('executeOperation -> updatedFormData ', updatedFormData)
          operation = {
            ...operation,
            formDataObject: updatedFormData,
            body: newFormData
          };
        }

        let request$: Observable<any>;

        switch (operation?.method?.toUpperCase()) {
          case 'POST':
            request$ = this.http.post(operation?.url, operation?.body);
            break;
          case 'PUT':
            request$ = this.http.put(operation?.url, operation?.body);
            break;
          case 'DELETE':
            request$ = this.http.delete(operation?.url);
            break;
          default:
            return of(markOperationExecutedWithError({
              id: operation?.id ?? null,
              error: operation?.error,
              data: {},
            }));
        }

        return request$.pipe(
          timeout(30000),
          switchMap(async (response) => {
            this.pendingOperations--;

            if (this.pendingOperations === 0 && this.syncPopover) {
              this.syncPopover.dismiss();
              this.syncPopover = null;
            }

            if (operation?.tempId) {
              this.processTempIdMappings(operation, response);
            }

            this.completedOperations++;
            const progressPercent = Math.floor((this.completedOperations / this.totalOperations) * 100);

            this.popoverDataService.updateInputData({
              progress: progressPercent,
              totalOperations: this.totalOperations,
              completedOperations: this.completedOperations
            });

            return markOperationExecuted({
              id: operation?.id ?? null,
              error: null,
              data: response,
              is_production_state: operation.url.includes('insert-many-productions')
            });
          }),
          catchError((error) => {
            this.pendingOperations--;

            if (this.pendingOperations === 0 && this.syncPopover) {
              this.popoverDataService.updateInputData({
                progress: 0,
                totalOperations: 0,
                completedOperations: 0
              });
              this.syncPopover.dismiss();

              this.syncPopover = null;
            }

            return of(markOperationExecutedWithError({
              id: operation?.id ?? null,
              error: error.error || error,
              data: {},
            }));
          })
        );
      })
    )
  );

  loadAppData$ = createEffect((): Observable<Action> =>
    this.actions$.pipe(
      ofType(loadAppData),
      concatMap(() => {
        const startDate = new Date(
          new Date().getFullYear(),
          new Date().getMonth() - 1,
          1
        ).toISOString();
        const endDate = new Date(
          new Date().getFullYear(),
          new Date().getMonth(),
          0 + 1
        ).toISOString();

        const date = { startDate: startDate, endDate: endDate };
        let dashboardData = {
          startDate: new Date(
            new Date().getFullYear(),
            1,
            1
          ).toISOString(),
          endDate: new Date(
            new Date().getFullYear(),
            11,
            31
          ).toISOString(),
          cropId: 1,
          groupBy: 'm-Y',
        };
        return [
          loadFarm(),
          loadTotalAccountingCategories({ data: date }),
          loadFamilyMember(),
          loadAgriProduct(),
          loadAnimal(),
          loadSelectedAgriProduct(),
          loadSelectedAnimal(),
          loadPlots(),
          loadPartnerType(),
          loadPartner(),
          loadUnit(),
          getDashboardData({ data: dashboardData })
        ]
      }), catchError((error) => {
        return of({ type: '[Connection] SYNC_PAUSED_DUE_TO_NETWORK' });
      })
    )
  );

  replaceTempIds$ = createEffect(
    (): Observable<any> =>
      this.actions$.pipe(
        ofType(replaceTempIds),
        withLatestFrom(this.store.select('connection')),
        map(([action, connection]) => {
          const request = action.request;
          const idMappings = connection.idMappings;
          const clonedRequest = cloneDeep(request);
          if (clonedRequest.body) {
            replaceTempIdsInObject(clonedRequest.body, idMappings);
          }
          if (clonedRequest.formDataObject) {
            const updatedFormData = replaceTempIdsInObject(clonedRequest.formDataObject, idMappings);

            if (clonedRequest.body instanceof FormData) {
              const newFormData = new FormData();
              for (const key in updatedFormData) {
                newFormData.append(key, updatedFormData[key]);
              }
              clonedRequest.body = newFormData;
            }
          }

          if (clonedRequest.url) {
            clonedRequest.url = replaceTempIdsInUrl(clonedRequest.url, idMappings);
          }

          return clonedRequest;
        })
      )
  );


  tempIds: IdMapping[] = [];
  // Process temporary ID mappings from the response
  private processTempIdMappings(operation: OfflineOperation, response: any): void {
    if (!operation.tempId || !response) return;

    const tempId = operation.tempId;
    let serverId = null;

    console.log(`Processing temp ID: ${tempId} () with response:`, response);

    if (response?.id) {
      serverId = response.id;
      console.log(`Mapping direct ID: ${tempId} -> ${serverId}`);
    }
    else if (Array.isArray(response)) {
      response.forEach(val => {
        let tempId = 'offline-' + val?.morphable_type + '-' + val?.morphable_id
        let serverId = val?.production_per_farm_id
        if (!this.tempIds.some((mapping) => mapping.tempId == tempId)) {
          this.tempIds.push({
            tempId,
            serverId,
            timestamp: Date.now()
          });
          // this.store.dispatch(addIdMapping({
          //   mapping: {
          //     tempId,
          //     serverId,
          //     timestamp: Date.now()
          //   }
          // }));
        }
      })

    }

    else if (typeof response === 'object') {

      serverId = this.findServerIdInObject(response);
      if (serverId) {
        console.log(`Mapping from complex object: ${tempId} -> ${serverId}`);
      }
    }


    if (serverId && !this.tempIds.some((mapping) => mapping.tempId == tempId)) {
      this.tempIds.push({
        tempId,
        serverId,
        timestamp: Date.now()
      });
      // this.store.dispatch(addIdMapping({
      //   mapping: {
      //     tempId,
      //     serverId,
      //     timestamp: Date.now()
      //   }
      // }));
    } else {
      console.warn(`No server ID found for temp ID: ${tempId} in response:`, response);
    }

  }

  private findServerIdInObject(obj: any): string | number | null {
    if (!obj || typeof obj !== 'object') return null;

    if (obj.id) {
      return obj.id;
    }
    if (obj.data && typeof obj.data === 'object') {
      const dataId = this.findServerIdInObject(obj.data); // Recursive call for data object
      if (dataId) return dataId;
    }

    if (Array.isArray(obj.results)) {
      for (const item of obj.results) {
        const itemId = this.findServerIdInObject(item);
        if (itemId) return itemId;
      }
    }

    return null;
  }
}
